import { useEffect, useState, useMemo } from 'react'
import Table from '../../components/Table';
import Table2 from '../../components/Table2';
import BarChart from '../../components/chartTypes/Barchart';
import { Column } from 'react-table';
import MultiSelectCheckboxGroup from '../../components/CheckBoxFilter';
import {generatePortfolioSummary, generatePortfolioMovement, generatePortfolioSummaryOverAll } from "../../services/summaryReport";
import { useTranslation } from 'react-i18next';
import { sixMonthsAgoFormatted, twoDaysAgoFormatted } from '../../constants/intex';
import { baseURL } from '../../services';
import Loader from '../../components/Loader';
import { listBranches } from '../../services/filterApis';
import Table3 from '../../components/Table3';

// Updated Branch colors for comparative mode
const BRANCH_COLORS = [
  { 
    bgColor: '#E3F2FD',  // Light Blue background
    textColor: '#0D47A1', // Dark Blue text
    borderColor: '#90CAF9' // Blue border
  },
  { 
    bgColor: '#E8F5E9',  // Light Green background
    textColor: '#1B5E20', // Dark Green text
    borderColor: '#A5D6A7' // Green border
  },
  { 
    bgColor: '#FFF3E0',  // Light Orange background
    textColor: '#E65100', // Dark Orange text
    borderColor: '#FFCC80' // Orange border
  }
];

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

const PortfolioOverall = () => {
  const { t } = useTranslation();

  interface Order {
    period: string;
    active_loan_count: string;
    tot_princ_os_amt_formatted: string;
    princ_os_amt_per_account: string;
    emi_loans_percentage: string;
  }

  interface OrderSummary {
    period:string;
    closure_amt_formatted: string;
    repayment_amt_formatted: string;
    new_origination_principal_os_formatted: string;
    net_movement_formatted: string;
    net_movement: string;
  }
  
  const [portfolioSummary, setPortfolioSummary] = useState<any[]>([]);
  const [portfolioMovement, setPortfolioMovement] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [portfolioMovementLoading, setPortfolioMovementLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>({});
  
  const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>(() => ({
    periodWithOutDay: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"],
  }));

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(() => ({
    periodWithOutDay: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"],
  }));

  const [applyFilter, setApplyFilter] = useState(0);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [comparativeTables, setComparativeTables] = useState<any[][]>([]);
  const [comparativeMovementTables, setComparativeMovementTables] = useState<any[][]>([]);

  const filters = [
    { key: "state", type: "select", label: t("filters.state") },
    { key: "cluster", type: "select", label: t("filters.cluster") },
    { key: "displaymode", type: "singleSelect", label: t("filters.displaymode") },
    { key: "branch", type: "select", label: t("filters.branch") },
    { key: "periodWithOutDay", type: "singleSelect", label: t("filters.period") },
    { key: "fromDate", type: "date", label: t("filters.fromDate"), maxValue: selectedFilters.toDate },
    { key: "toDate", type: "date", label: t("filters.toDate"), maxValue: twoDaysAgoFormatted },
  ];
  
  const [branchList, setBranchList] = useState<{ branch_id: string; branch_name: string }[]>([]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(branchList)) {
      branchList.forEach((branch) => {
        const id = branch?.branch_id;
        const name = branch?.branch_name;
        if (id && name) {
          map[id] = name;
        }
      });
    }
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    if (branchIdToNameMap[branchId]) return branchIdToNameMap[branchId];
    const branch = branchList.find((b) => b.branch_id === branchId);
    if (branch) return branch.branch_name || branchId;
    return branchId;
  };

  const generatePortfolioSummaryApi = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setLoading(true);

    try {
      if (isComparative) {
        setComparativeLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        // Assign colors to branches
        const colors: Record<string, BranchColor> = {};
        selectedBranches.forEach((branchId, index) => {
          colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
        });
        setBranchColors(colors);

        const branchPromises = selectedBranches.map((branchId) =>
          generatePortfolioSummaryOverAll({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        const comparativeData = branchResponses.map((res) =>
          Array.isArray(res.data) ? res.data : []
        );

        setComparativeTables(comparativeData);

        // Fetch portfolioSummary for period keys
        const periodResponse = await generatePortfolioSummaryOverAll({
          ...selectedFilters,
          branch: [], // remove branch to get all-periods view
        });

        const periodData = Array.isArray(periodResponse.data)
          ? periodResponse.data
          : [];

        setPortfolioSummary(periodData);
      } else {
        const response = await generatePortfolioSummaryOverAll(selectedFilters);
        setPortfolioSummary(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error(err);
    } finally {
      setLoading(false);
      setComparativeLoading(false);
    }
  };

  const generatePortfolioNetMovement = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setPortfolioMovementLoading(true);

    try {
      if (isComparative) {
        setComparativeLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        const branchPromises = selectedBranches.map((branchId) =>
          generatePortfolioMovement({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        const comparativeData = branchResponses.map((res) =>
          Array.isArray(res.data) ? res.data : []
        );

        setComparativeMovementTables(comparativeData);

        // Fetch portfolioMovement for period keys
        const periodResponse = await generatePortfolioMovement({
          ...selectedFilters,
          branch: [], // remove branch to get all-periods view
        });

        const periodData = Array.isArray(periodResponse.data)
          ? periodResponse.data
          : [];

        setPortfolioMovement(periodData);
      } else {
        const response = await generatePortfolioMovement(selectedFilters);
        setPortfolioMovement(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error(err);
    } finally {
      setPortfolioMovementLoading(false);
      setComparativeLoading(false);
    }
  };

  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        return;
      }
    }

    setSelectedFilters(pendingFilters);
    setValidationError("");
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter((prev) => prev + 1);
  };

  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  const handleCancelClick = () => {
    setSelectedFilters({
      periodWithOutDay: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
    setApplyFilter(0);
    setPendingFilters({
      periodWithOutDay: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
  };

  const handleDownloadClick = () => {   
    const link = `${baseURL}generate/portfolio_summary/list_csv?from_date=${selectedFilters.fromDate ?? ""}&to_date=${selectedFilters.toDate ?? ""}&group_by=${selectedFilters.periodWithOutDay ?? ""}&state_id=${selectedFilters.state ?? ""}&territory_id=${selectedFilters.territory ?? ""}&cluster_id=${selectedFilters.cluster ?? ""}&branch_id=${selectedFilters.branch ?? ""}`;
    window.location.href = link;
  }

  const columns: Column<any>[] = [
    { Header: "Period", accessor: "period" },
    { Header: "#Loans", accessor: "active_loan_count" },
    { Header: "PE O/S", accessor: "tot_princ_os_amt_formatted" },
    { Header: "PE O/S per account", accessor: "princ_os_amt_per_account" },
    { Header: "EMI Loans % portfolio", accessor: "emi_loans_percentage" },
  ];

  const NetMovementColumns: Column<OrderSummary>[] = [
    { Header: "Period", accessor: "period" },
    { Header: "Closures(-)", accessor: "closure_amt_formatted" },
    { Header: "Repayments(-)", accessor: "repayment_amt_formatted" },
    { Header: "New Origination PE O/S (+)", accessor: "new_origination_principal_os_formatted" },
    { Header: "Net Movement", accessor: "net_movement_formatted" },
  ];

  useEffect(() => {
    generatePortfolioSummaryApi();
    generatePortfolioNetMovement();
  }, [applyFilter]);

  const periodMap: Record<string, string> = {
    w: t("portfolioOverall.w"),
    m: t("portfolioOverall.m"),
    q: t("portfolioOverall.q"),
    d: t("portfolioOverall.d"),
  };

  return (
    <div>
      {/* Page Content */}
      <div className="page-content">
        {/* Dynamic Grey Header */}
        <div className="header">
          {t('portfolioOverall.pageTitle')} 
        </div>

        {/* Main Content */}
        <div className="content px-3">
          <MultiSelectCheckboxGroup 
            filters={filters} 
            selectedValues={pendingFilters}
            onChange={handleFilterChange} 
            isLoading={[loading, portfolioMovementLoading, comparativeLoading]}
            buttons={[
              { label: t("buttons.apply"), onClick: () => handleApplyClick()},
              { label: t("buttons.cancel"), onClick: () => handleCancelClick()},
              { 
                label: t("buttons.download"), 
                onClick: () => handleDownloadClick(),
                disabled: isDownloadDisabled
              },
            ]}
          />
          {/* Show validation error if exists */}
          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>

        {/* Portfolio Summary Section */}
        <div className="p-3">
          {!validationError && (
            <>
              {applyFilter &&
              selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2" ? (
                <div style={{ width: "100%", overflow: "auto" }}>
                  {(() => {
                    // Create combined data structure
                    const branchDataMap = new Map<string, any[]>();
                    (selectedFilters.branch || []).forEach((branchId, index) => {
                      branchDataMap.set(branchId, comparativeTables?.[index] || []);
                    });

                    // Create combined data using period match
                    const combinedData = (portfolioSummary || []).map((periodData) => {
                      const row: any = { period: periodData.period };

                      (selectedFilters.branch || []).forEach((branchId) => {
                        const branchData = branchDataMap.get(branchId) || [];
                        const matchingPeriodData = branchData.find(
                          (d: any) => d.period === periodData.period
                        ) || {};

                        row[`branch_${branchId}`] = {
                          activeLoanCount: matchingPeriodData.active_loan_count || "-",
                          totPrincOsAmt: matchingPeriodData.tot_princ_os_amt_formatted || "-",
                          princOsAmtPerAccount: matchingPeriodData.princ_os_amt_per_account || "-",
                          emiLoansPercentage: matchingPeriodData.emi_loans_percentage || "-",
                        };
                      });

                      return row;
                    });

                    return (
                      <Table2
                        columns={[
                          // Period column
                          {
                            Header: "Period",
                            accessor: "period",
                            sticky: "left",
                            style: {
                              fontWeight: "bold",
                              borderRight: "1px solid #000",
                            },
                          },
                          // #Loans group
                          {
                            Header: "#Loans",
                            headerStyle: {
                              borderBottom: "1px solid #000",
                              borderRight: "1px solid #000",
                            },
                            columns:
                              selectedFilters.branch?.map((branchId) => ({
                                Header: getBranchName(branchId),
                                accessor: `branch_${branchId}.activeLoanCount`,
                                Cell: ({ value }: { value?: string }) => value || "-",
                                style: {
                                  backgroundColor: branchColors[branchId]?.bgColor || "#f5f5f5",
                                  borderRight: "1px solid #ddd",
                                  borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                },
                              })) || [],
                          },
                          // PE O/S group
                          {
                            Header: "PE O/S",
                            headerStyle: {
                              borderBottom: "1px solid #000",
                              borderRight: "1px solid #000",
                            },
                            columns:
                              selectedFilters.branch?.map((branchId) => ({
                                Header: getBranchName(branchId),
                                accessor: `branch_${branchId}.totPrincOsAmt`,
                                Cell: ({ value }: { value?: string }) => value || "-",
                                style: {
                                  backgroundColor: branchColors[branchId]?.bgColor || "#f9f9f9",
                                  borderRight: "1px solid #ddd",
                                  borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                },
                              })) || [],
                          },
                          // PE O/S per account group
                          {
                            Header: "PE O/S per account",
                            headerStyle: {
                              borderBottom: "1px solid #000",
                              borderRight: "1px solid #000",
                            },
                            columns:
                              selectedFilters.branch?.map((branchId) => ({
                                Header: getBranchName(branchId),
                                accessor: `branch_${branchId}.princOsAmtPerAccount`,
                                Cell: ({ value }: { value?: string }) => value || "-",
                                style: {
                                  backgroundColor: branchColors[branchId]?.bgColor || "#f5f5f5",
                                  borderRight: "1px solid #ddd",
                                  borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                },
                              })) || [],
                          },
                          // EMI Loans % portfolio group
                          {
                            Header: "EMI Loans % portfolio",
                            headerStyle: {
                              borderBottom: "1px solid #000",
                            },
                            columns:
                              selectedFilters.branch?.map((branchId) => ({
                                Header: getBranchName(branchId),
                                accessor: `branch_${branchId}.emiLoansPercentage`,
                                Cell: ({ value }: { value?: string }) => value || "-",
                                style: {
                                  backgroundColor: branchColors[branchId]?.bgColor || "#f9f9f9",
                                  borderRight: "1px solid #ddd",
                                  borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                },
                              })) || [],
                          },
                        ]}
                        data={combinedData}
                        tableHead={t('portfolioOverall.tableHead1')}
                        type="1"
                        loading={comparativeLoading || loading}
                        isComparative={true}
                        branchCount={selectedFilters.branch?.length || 1}
                        branchColors={branchColors}
                        branchIds={selectedFilters.branch || []}
                      />
                    );
                  })()}
                </div>
              ) : (
                <div style={{ width: "100%", overflow: "scroll" }}>
                  <Table 
                    columns={columns} 
                    data={portfolioSummary} 
                    tableHead={t('portfolioOverall.tableHead1')}
                    type="1"
                    loading={loading}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Portfolio Movement Section */}
        <div className="p-3 mt-5">
          {!validationError && (
            <>
              {applyFilter &&
              selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2" ? (
                <div style={{ width: "100%", overflow: "auto" }}>
                  {(() => {
                    // Create combined data structure
                    const branchDataMap = new Map<string, any[]>();
                    (selectedFilters.branch || []).forEach((branchId, index) => {
                      branchDataMap.set(branchId, comparativeMovementTables?.[index] || []);
                    });

                    // Create combined data using period match
                    const combinedData = (portfolioMovement || []).map((periodData) => {
                      const row: any = { period: periodData.period };

                      (selectedFilters.branch || []).forEach((branchId) => {
                        const branchData = branchDataMap.get(branchId) || [];
                        const matchingPeriodData = branchData.find(
                          (d: any) => d.period === periodData.period
                        ) || {};

                        row[`branch_${branchId}`] = {
                          closureAmt: matchingPeriodData.closure_amt_formatted || "-",
                          repaymentAmt: matchingPeriodData.repayment_amt_formatted || "-",
                          newOrigination: matchingPeriodData.new_origination_principal_os_formatted || "-",
                          netMovement: matchingPeriodData.net_movement_formatted || "-",
                          netMovementValue: matchingPeriodData.net_movement || 0,
                        };
                      });

                      return row;
                    });

                    return (
                      <>
                        <Table3
                          columns={[
                            // Period column
                            {
                              Header: "Period",
                              accessor: "period",
                              sticky: "left",
                              style: {
                                fontWeight: "bold",
                                borderRight: "1px solid #000",
                              },
                            },
                            // Closures(-) group
                            {
                              Header: "Closures(-)",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                                borderRight: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.closureAmt`,
                                  Cell: ({ value }: { value?: string }) => value || "-",
                                  style: {
                                    backgroundColor: branchColors[branchId]?.bgColor || "#f5f5f5",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                  },
                                })) || [],
                            },
                            // Repayments(-) group
                            {
                              Header: "Repayments(-)",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                                borderRight: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.repaymentAmt`,
                                  Cell: ({ value }: { value?: string }) => value || "-",
                                  style: {
                                    backgroundColor: branchColors[branchId]?.bgColor || "#f9f9f9",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                  },
                                })) || [],
                            },
                            // New Origination PE O/S (+) group
                            {
                              Header: "New Origination PE O/S (+)",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                                borderRight: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.newOrigination`,
                                  Cell: ({ value }: { value?: string }) => value || "-",
                                  style: {
                                    backgroundColor: branchColors[branchId]?.bgColor || "#f5f5f5",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                  },
                                })) || [],
                            },
                            // Net Movement group
                            {
                              Header: "Net Movement",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.netMovement`,
                                  Cell: ({ value }: { value?: string }) => value || "-",
                                  style: {
                                    backgroundColor: branchColors[branchId]?.bgColor || "#f9f9f9",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
                                  },
                                })) || [],
                            },
                          ]}
                          data={combinedData}
                          tableHead={t('portfolioOverall.tableHead2')}
                          type="1"
                          loading={comparativeLoading || portfolioMovementLoading}
                          isComparative={true}
                          branchCount={selectedFilters.branch?.length || 1}
                          branchColors={branchColors}
                          branchIds={selectedFilters.branch || []}
                        />

                        {/* Combined Chart for Comparative Mode */}
                        <div className="mt-5 p-5" style={{marginTop: "20px"}}>
                          <BarChart 
                            title={t('portfolioOverall.title')}
                            labels={portfolioMovement.map(item => item.period)}
                            dataSets={
                              selectedFilters.branch?.map((branchId, index) => {
                                const branchData = comparativeMovementTables[index] || [];
                                const branchName = getBranchName(branchId);
                                const color = BRANCH_COLORS[index % BRANCH_COLORS.length];
                                
                                return {
                                  label: branchName,
                                  data: portfolioMovement.map(periodItem => {
                                    const branchPeriodData = branchData.find(
                                      (d: any) => d.period === periodItem.period
                                    );
                                    return branchPeriodData?.net_movement || 0;
                                  }),
                                  backgroundColor: color.borderColor,
                                  borderColor: color.borderColor,
                                  borderWidth: 1
                                };
                              }) || []
                            }
                            xLabel={periodMap[selectedFilters.periodWithOutDay?.[0]] || "Period"}
                            yLabel={t('portfolioOverall.yLabel')}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <div style={{ width: "100%", overflow: "scroll" }}>
                    <Table 
                      columns={NetMovementColumns} 
                      data={portfolioMovement} 
                      tableHead={t('portfolioOverall.tableHead2')}
                      type="1"
                      loading={portfolioMovementLoading}
                    />
                  </div>

                  {/* Chart for Consolidated Mode */}
                  <div className="mt-5 p-5" style={{marginTop: "20px"}}>
                    <BarChart 
                      title={t('portfolioOverall.title')}
                      labels={portfolioMovement.map(item => item.period)}
                      dataSets={[
                        { 
                          label: t('portfolioOverall.label'), 
                          data: portfolioMovement.map(item => item.net_movement), 
                          backgroundColor: "grey" 
                        }
                      ]}
                      xLabel={periodMap[selectedFilters.periodWithOutDay?.[0]] || "Period"}
                      yLabel={t('portfolioOverall.yLabel')}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PortfolioOverall;